import React from "react";
import { findIndex, map } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import {
  activeRolloverAccountSameAsCurrent,
  newActiveRolloverAccount,
  activeRolloverIsMixed,
  userIs401kRollIn,
} from "store/selectors/user";
import {
  adminSteps,
  employerMultiAccountSteps,
  employerMultiWithBillingSteps,
  employerOnboardingSteps,
  employerOnboardingWithBillingSteps,
  individualOnboardingSteps,
  IndividualRolloverSameAsCurrent,
  IndividualMultiAccountSteps,
  paychexOnboardingSteps,
} from "statics/onboardingSteps";
import type { RootState } from "types/state";

import CircularProgressBar from "./CircularProgressBar";
import ProgressItem from "components/ProgressItem";

/** A single onboarding step as defined in statics/onboardingSteps. Some steps
 * (e.g. INDIVIDUAL_WAITING_APPROVAL) only carry `id` + `hidden`, while the rest
 * carry `id`/`label`/`stepTime`; all fields are optional to cover both shapes. */
interface OnboardingStep {
  id: string;
  label?: string;
  stepTime?: number;
  hidden?: boolean;
}

interface ProgressBarOwnProps {
  isEmployer?: boolean;
  isAdministrator?: boolean;
  isMultiAccount?: boolean;
  showBillingRoute?: boolean;
  activeStepId?: string;
  progressPercent?: string;
}

const mapStateToProps = (state: RootState) => {
  return {
    isRolloverAccount: newActiveRolloverAccount(state),
    isRolloverSameAsCurrent: activeRolloverAccountSameAsCurrent(state),
    isRolloverIsMixed: activeRolloverIsMixed(state),
    isPaychex: userIs401kRollIn(state),
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type ProgressBarProps = PropsFromRedux & ProgressBarOwnProps;

class ProgressBar extends React.Component<WithApolloClient<ProgressBarProps>> {
  _buildStepsSection(steps: OnboardingStep[]) {
    const { activeStepId } = this.props;
    const stepItems = map(steps, (step, idx) => {
      const activeStepIdx = findIndex(steps, { id: activeStepId });
      const currentStepIdx = findIndex(steps, { id: step.id });
      const isActive = step.id === activeStepId;
      const isComplete = currentStepIdx < activeStepIdx;

      /*
       individual has a few states where user has done all steps and is in a waiting period/additional work
       so want to show previous states were completed and not show the transitory step in the progress bar
       */

      if (step.hidden) {
        return null;
      }

      return (
        <ProgressItem
          key={step.id}
          isComplete={isComplete}
          isActive={isActive}
          stepNum={idx + 1}
          stepLabel={step.label}
          stepTime={step.stepTime}
          // @ts-expect-error preserve pre-existing quirk: `hidden` is passed
          // through but ProgressItem never declares or uses it (no-op prop).
          hidden={step.hidden}
        />
      );
    });

    return (
      <section className="steps-bar-module">
        <article className="steps-cell">
          <ul>{stepItems}</ul>
        </article>
        <article className="progress-cell">
          <CircularProgressBar
            strokeWidth="8"
            sqSize="100"
            percentage={this.props.progressPercent}
          />
        </article>
      </section>
    );
  }

  render() {
    if (this.props.isAdministrator) {
      return this._buildStepsSection(adminSteps);
    } else if (this.props.isEmployer) {
      if (this.props.isMultiAccount) {
        return this._buildStepsSection(
          this.props.showBillingRoute
            ? employerMultiWithBillingSteps
            : employerMultiAccountSteps
        );
      } else {
        return this._buildStepsSection(
          this.props.showBillingRoute
            ? employerOnboardingWithBillingSteps
            : employerOnboardingSteps
        );
      }
    } else {
      if (this.props.isRolloverAccount || this.props.isRolloverIsMixed) {
        return this._buildStepsSection(IndividualMultiAccountSteps);
      } else if (this.props.isRolloverSameAsCurrent) {
        return this._buildStepsSection(IndividualRolloverSameAsCurrent);
      } else if (this.props.isPaychex) {
        return this._buildStepsSection(paychexOnboardingSteps);
      } else {
        return this._buildStepsSection(individualOnboardingSteps);
      }
    }
  }
}

export default connector(withApollo<ProgressBarProps>(ProgressBar));
